import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import '../styles/Rules.css';
import SEO from './SEO';

const renderList = (items) => {
  if (!Array.isArray(items)) return null;
  return (
    <ul className="rules-list rules-points-list">
      {items.map((item, index) => {
        // Items like "10 b — text" or "10 pts — text" get a badge.
        const match = item.match(/^(\d+\s*(?:b|pts))\s*—\s*(.+)$/);
        if (match) {
          const points = parseInt(match[1], 10);
          return (
            <li key={index} className={`rules-point-item points-${points}`}>
              <span className="points-badge">{match[1]}</span>
              <span className="points-text">{match[2]}</span>
            </li>
          );
        }
        return <li key={index}>{item}</li>;
      })}
    </ul>
  );
};

const Rules = () => {
  const { t, i18n } = useTranslation();

  return (
    <div className="rules-container">
      <SEO
        title={t('rules.title', { defaultValue: 'Pravidla hry' })}
        description="Přečtěte si kompletní pravidla tipovačky Scoreo. Jak bodujeme přesné výsledky, rozdíly skóre a vítěze zápasu."
      />

      <nav className="rules-toc" aria-label={t('rules.tableOfContents', { defaultValue: 'Obsah' })}>
        <h3 className="rules-toc-title">{t('rules.tableOfContents', { defaultValue: 'Obsah' })}</h3>
        <ul className="rules-toc-list">
          <li><a href="#how-to-play">{t('rules.howToPlay', 'Jak hrát / How to Play')}</a></li>
          <li><a href="#scoring">{t('rules.scoring')}</a></li>
          <li><a href="#special-situations">{t('rules.special.title')}</a></li>
          <li><a href="#try-it">{t('rules.tryIt.title')}</a></li>
          <li><a href="#ai-agent">{t('rules.aiAgent.title')}</a></li>
          <li><a href="#tips-deadline">{t('rules.tipsDeadline')}</a></li>
        </ul>
      </nav>

      <section className="rules-section" id="how-to-play">
        <h3>{t('rules.howToPlay', 'Jak hrát / How to Play')}</h3>
        <p>{t('rules.description')}</p>
      </section>

      <section className="rules-section" id="scoring">
        <h3>{t('rules.scoring')}</h3>
        <p>{t('rules.intro')}</p>

        {/* === FOTBAL === */}
        <div className="sport-block sport-football">
          <h4 className="sport-heading">{t('rules.football.title')}</h4>

          <div className="rules-subsection">
            <h5>{t('rules.football.standard.title')}</h5>
            <p>{t('rules.football.standard.intro')}</p>
            {renderList(t('rules.football.standard.items', { returnObjects: true }))}
          </div>

          <div className="rules-subsection">
            <h5>{t('rules.football.knockout.title')}</h5>
            <p>{t('rules.football.knockout.intro')}</p>

            <p className="scenario-label">{t('rules.football.knockout.wentToOT')}</p>
            {renderList(t('rules.football.knockout.wentToOT_items', { returnObjects: true }))}

            <p className="scenario-label">{t('rules.football.knockout.noOT')}</p>
            <p>{t('rules.football.knockout.noOT_text')}</p>

            <p className="scenario-label">{t('rules.football.knockout.mismatch')}</p>
            {renderList(t('rules.football.knockout.mismatch_items', { returnObjects: true }))}
          </div>

          <div className="rules-subsection">
            <h5>{t('rules.football.secondLeg.title')}</h5>
            <p>{t('rules.football.secondLeg.desc')}</p>
          </div>
        </div>

        {/* === HOKEJ === */}
        <div className="sport-block sport-hockey">
          <h4 className="sport-heading">{t('rules.hockey.title')}</h4>
          <p className="sport-intro">{t('rules.hockey.intro')}</p>

          <div className="rules-subsection">
            <h5>{t('rules.hockey.regulation.title')}</h5>
            {renderList(t('rules.hockey.regulation.items', { returnObjects: true }))}
          </div>

          <div className="rules-subsection">
            <h5>{t('rules.hockey.overtime.title')}</h5>
            {renderList(t('rules.hockey.overtime.items', { returnObjects: true }))}
            <p className="subsection-note">{t('rules.hockey.overtime.note')}</p>
          </div>

          <div className="rules-subsection">
            <h5>{t('rules.hockey.mismatch.title')}</h5>
            {renderList(t('rules.hockey.mismatch.items', { returnObjects: true }))}
          </div>
        </div>

        {i18n.language === 'cs' ? (
          <img src="/rules_sample_cz.png" alt="Ukázka pravidel" />
        ) : (
          <img src="/rules_sample_en.png" alt="Rules sample" />
        )}
      </section>

      {/* === ZVLÁŠTNÍ SITUACE === */}
      <section className="rules-section" id="special-situations">
        <h3>{t('rules.special.title')}</h3>
        {renderList(t('rules.special.items', { returnObjects: true }))}
      </section>

      {/* === VYZKOUŠEJ SI === */}
      <section className="rules-section try-it-section" id="try-it">
        <h3>{t('rules.tryIt.title')}</h3>
        <p>
          {t('rules.tryIt.before')}
          <Link to="/scoring">{t('rules.tryIt.linkLabel')}</Link>
          {t('rules.tryIt.after')}
        </p>
      </section>

      <section className="rules-section ai-agent-section" id="ai-agent">
        <h3>{t('rules.aiAgent.title')}</h3>
        <p>{t('rules.aiAgent.description')}</p>
        <ul>
          {(t('rules.aiAgent.points', { returnObjects: true }) || []).map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="rules-section" id="tips-deadline">
        <h3>{t('rules.tipsDeadline')}</h3>
        <p>{t('rules.tipDeadline')}</p>
      </section>
    </div>
  );
};

export default Rules;
